"use client";

import { useState } from "react";
import type { Conversation, UserSummary } from "@/lib/api/messages";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import {
  useAddParticipants,
  useDeclineRequest,
  useLeaveConversation,
  useMarkConversationUnread,
  useMuteConversation,
  useRenameConversation,
} from "@/lib/hooks/useConversation";
import {
  useBlockUser,
  useIsBlocked,
  useUnblockUser,
} from "@/lib/hooks/useMessageRequests";
import { useDismissable } from "@/lib/hooks/useDismissable";
import { useAnchoredPosition } from "@/lib/hooks/useAnchoredPosition";
import { conversationTitle, otherParticipants, personName } from "./participants";
import { ParticipantPicker } from "./ParticipantPicker";
import { ReportDialog } from "./ReportDialog";

/** Which "are you sure?" prompt is currently open, if any. */
type PendingConfirm = "leave" | "block" | "delete" | null;

/** The server's own bound on `Conversation.name`; the field stops there too. */
const MAX_NAME_LENGTH = 80;

/**
 * How tall to assume the dropdown is when choosing which side of the ⋮ it
 * opens on. A guess is all that is available — the side is decided as the menu
 * opens, before it has rendered — and it is deliberately the FULL menu:
 * mark unread, mute, name, add, block, report, delete, leave is eight rows of
 * 42px plus the list's padding. Erring high only means a menu that would just
 * about have fitted below opens upward instead.
 */
const MENU_HEIGHT_ESTIMATE = 360;

/**
 * The report picker's own cap, in px — what `max-h-72` used to say. A design
 * choice rather than a placement one: a group with forty participants must not
 * open a menu the height of the screen. It is now taken together with the room
 * actually available, since a cap taller than the viewport caps nothing.
 */
const REPORT_PICKER_MAX_HEIGHT = 288;

/**
 * The ⋮ menu for a conversation: mark unread, mute, name it, add participants,
 * block, report, delete, leave.
 *
 * Its own component because the conversation now has two frames — the page
 * header and a docked chat window — and "you can report spam from the page but
 * not from the tray" is exactly the kind of gap that appears when a menu is
 * built into one of them.
 */
export function ConversationMenu({
  conversation,
  compact = false,
  onGone,
}: {
  conversation: Conversation;
  /** Smaller trigger for the chat dock's 48px header. */
  compact?: boolean;
  /**
   * Called after leaving, blocking, deleting or marking unread succeeds — i.e.
   * once this conversation is no longer something the current user should keep
   * looking at. The docked chat window closes itself on this; the full page
   * navigates back to the inbox. Without it, blocking or deleting the very
   * conversation you have open leaves it sitting on screen as if nothing
   * happened — and marking one unread would leave the member staring at the
   * thread they just said they had not got to.
   */
  onGone?: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // `null` = the naming dialog is closed. A string — including "" — is the
  // draft, so opening it on an unnamed conversation is not the same state as
  // it being shut.
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  // In a group conversation "report" has to ask who; in a 1:1 there is only
  // one possible answer, so it is skipped.
  const [pickingReportTarget, setPickingReportTarget] = useState(false);
  const [reportUserId, setReportUserId] = useState<number | undefined>();
  const [picked, setPicked] = useState<UserSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<PendingConfirm>(null);
  /**
   * THE ERROR POPOVER IS DISMISSABLE TOO, and that is what bounds it.
   *
   * `error` is only ever cleared at the top of the NEXT action, so a failed
   * mute left the message on screen for the life of the component — and, once
   * the popover started being placed by `useAnchoredPosition` below, left that
   * subscribed to `scroll` and `resize` for just as long, re-measuring on every
   * scroll event. `ChatDock` keeps one of these mounted per open window.
   *
   * One outside click now ends both, which is what a popover should do anyway.
   */
  const menuRef = useDismissable<HTMLDivElement>(
    menuOpen || error !== null,
    () => {
      setMenuOpen(false);
      setError(null);
    },
    { escape: false },
  );

  /**
   * WHICH WAY THE DROPDOWN OPENS (OSO-555).
   *
   * It was `top-full` and nothing else, which is fine in the page header and
   * useless in the docked chat window: `ChatDock`'s row is `fixed bottom-0`, so
   * a MINIMIZED window is 48px of header sitting on the bottom edge of the
   * screen and "downward" is entirely off it. The menu opened every time; it
   * was just never on screen, which is why it read as doing nothing.
   *
   * Deliberately not a `minimized` special case — a short viewport, or a
   * taller dock header later, is the same bug with no flag to key off. The
   * shared placement maths already answers "does it fit, and if not what
   * gives"; this takes only its verdict and leaves the panels positioned in
   * CSS, since `right-0` alignment against the trigger is already correct and
   * only the vertical side was ever wrong.
   *
   * The error popover shares the verdict rather than measuring itself. It is
   * much shorter than the menu, so it can flip up a little sooner than it had
   * to — never later than it must, which is the direction that matters.
   *
   * NOTHING RENDERS UNTIL `anchored` HAS AN ANSWER. The hook measures in a
   * passive effect, so on the render that opens the menu it is still null; a
   * panel drawn on that frame takes the `top-full` fallback and is painted
   * below the screen edge before the effect flips it — one frame of exactly
   * the bug this fixes. A frame with no menu is invisible; a frame with the
   * menu in the wrong place is not.
   */
  const anchored = useAnchoredPosition(menuRef, menuOpen || error !== null, {
    preferred: "below",
    desiredHeight: MENU_HEIGHT_ESTIMATE,
  });
  const dropdownSide = anchored?.placement === "above" ? "bottom-full mb-1" : "top-full mt-1";

  const mute = useMuteConversation(conversation.id);
  const markUnread = useMarkConversationUnread(conversation.id);
  const leave = useLeaveConversation(conversation.id);
  const add = useAddParticipants(conversation.id);
  const rename = useRenameConversation(conversation.id);
  const block = useBlockUser(conversation.id);
  const deleteConversation = useDeclineRequest(conversation.id);
  const unblock = useUnblockUser();

  const others = otherParticipants(conversation.participants);
  const primary = others[0];

  // A BLOCK IS ON A PERSON now, not on a conversation — but a group thread has
  // no single "them" to name, so the control stays 1:1. That is a question of
  // whom, not of whether: nothing here decides permission.
  const canBlock = !conversation.isGroup && !!primary;
  // Asked about THIS person rather than filtered out of the inbox's blocked
  // list: that list is cursor-paged behind a "Vis flere" button, so a `.some()`
  // over its first page answered "not blocked" for everyone past row 20 and
  // offered "Bloker bruger" on a thread that was already blocked.
  const { data: blockedNow } = useIsBlocked(primary?.id, canBlock);
  const isBlocked = canBlock && blockedNow === true;
  const blockTargetName = primary ? personName(primary) : "personen";

  // Who a report defaults to. The roster now INCLUDES the viewer, so a creator
  // who is also someone else is found among `others`; when the current user
  // started the conversation, fall back to the first other participant rather
  // than letting the server reject "you cannot report yourself".
  const creator = conversation.createdBy;
  const reportTarget =
    creator && others.some((u) => u.id === creator.id) ? creator : primary;

  // `can.addParticipants` IS the creator-only rule, decided by the same
  // predicate the route enforces (D11) — never re-derived from who created the
  // conversation, which is what this used to do.
  const canAddParticipants = conversation.can.addParticipants;
  // And `can.rename` IS "my seat is live, and this is either a group thread or
  // one that already HAS a name", decided by the server. Counting participants
  // here would be the same mistake in a new place — and it would be wrong on
  // top of that: a named group that shrinks to two seats keeps its name, and
  // the two who stayed must still be able to change or clear it.
  const canRename = conversation.can.rename;

  async function handleMute() {
    setMenuOpen(false);
    setError(null);
    try {
      await mute.mutateAsync(!conversation.muted);
    } catch {
      setError("Indstillingen kunne ikke gemmes. Prøv igen.");
    }
  }

  /**
   * OSO-520. Ends by calling `onGone` — the page goes back to the inbox, the
   * dock closes — and that is the point rather than a side effect: the thread
   * is what the member has just said they have not dealt with yet, and leaving
   * it open in front of them is a thread they are still reading. It is also
   * what puts the unread row where they will meet it again.
   */
  async function handleMarkUnread() {
    setMenuOpen(false);
    setError(null);
    try {
      await markUnread.mutateAsync();
      onGone?.();
    } catch {
      setError("Samtalen kunne ikke markeres som ulæst. Prøv igen.");
    }
  }

  async function handleUnblock() {
    setMenuOpen(false);
    if (!primary) return;
    setError(null);
    try {
      await unblock.mutateAsync(primary.id);
    } catch {
      setError("Blokeringen kunne ikke fjernes. Prøv igen.");
    }
  }

  // Leave, block and delete all end with the current user no longer wanting
  // to look at this conversation, so they share one confirm step and one
  // error message and both call onGone on success.
  const confirmPending =
    (confirming === "leave" && leave.isPending) ||
    (confirming === "block" && block.isPending) ||
    (confirming === "delete" && deleteConversation.isPending);

  async function handleConfirmed() {
    const action = confirming;
    setError(null);
    try {
      if (action === "leave") await leave.mutateAsync();
      else if (action === "block" && primary) await block.mutateAsync(primary.id);
      else if (action === "delete") await deleteConversation.mutateAsync();
      setConfirming(null);
      onGone?.();
    } catch {
      setConfirming(null);
      setError(
        action === "leave"
          ? "Samtalen kunne ikke forlades. Prøv igen."
          : action === "block"
            ? "Personen kunne ikke blokeres. Prøv igen."
            : "Samtalen kunne ikke slettes. Prøv igen."
      );
    }
  }

  async function handleRename() {
    if (nameDraft === null) return;
    setError(null);
    try {
      // Blank clears the name and puts the participant list back — the server
      // stores an empty result as `null`, so this is a "remove the name" verb
      // rather than a way to make a header disappear.
      const trimmed = nameDraft.trim();
      await rename.mutateAsync(trimmed === "" ? null : trimmed);
      setNameDraft(null);
    } catch {
      setError("Navnet kunne ikke gemmes. Prøv igen.");
    }
  }

  async function handleAdd() {
    setError(null);
    try {
      await add.mutateAsync(picked.map((p) => p.id));
      setAddOpen(false);
      setPicked([]);
    } catch {
      setError("Deltagerne kunne ikke tilføjes. Prøv igen.");
    }
  }

  const iconSize = compact ? 16 : 20;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-label="Samtaleindstillinger"
        className={`${
          compact ? "p-1" : "p-2"
        } text-subtle-foreground hover:text-gray-900 dark:hover:text-gray-100 hover:bg-muted rounded-lg transition-colors`}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="12" r="1" />
          <circle cx="12" cy="19" r="1" />
        </svg>
      </button>

      {menuOpen && anchored && pickingReportTarget && (
        <div
          className={`absolute right-0 ${dropdownSide} w-64 overflow-y-auto bg-surface rounded-xl border border-border shadow-lg py-1 z-50`}
          style={{ maxHeight: Math.min(REPORT_PICKER_MAX_HEIGHT, anchored.maxHeight) }}
        >
          <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-subtle-foreground">
            Hvem vil du anmelde?
          </p>
          {others.map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => {
                setPickingReportTarget(false);
                setMenuOpen(false);
                setReportUserId(user.id);
                setReportOpen(true);
              }}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left text-foreground hover:bg-surface-2 transition-colors"
            >
              {personName(user)}
            </button>
          ))}
          <div className="border-t border-border mt-1 pt-1">
            <button
              type="button"
              onClick={() => setPickingReportTarget(false)}
              className="w-full px-4 py-2.5 text-sm text-left text-muted-foreground hover:bg-surface-2 transition-colors"
            >
              Tilbage
            </button>
          </div>
        </div>
      )}

      {menuOpen && anchored && !pickingReportTarget && (
        <div
          className={`absolute right-0 ${dropdownSide} w-64 overflow-y-auto bg-surface rounded-xl border border-border shadow-lg py-1 z-50`}
          style={{ maxHeight: anchored.maxHeight }}
        >
          {conversation.can.markUnread && (
          <button
            type="button"
            onClick={handleMarkUnread}
            disabled={markUnread.isPending}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left text-foreground hover:bg-surface-2 transition-colors disabled:opacity-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-subtle-foreground">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
            Markér som ulæst
          </button>
          )}

          {conversation.can.mute && (
          <button
            type="button"
            onClick={handleMute}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left text-foreground hover:bg-surface-2 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-subtle-foreground">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {conversation.muted
              ? "Slå notifikationer til"
              : "Slå notifikationer fra"}
          </button>
          )}

          {canRename && (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setNameDraft(conversation.name ?? "");
              }}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left text-foreground hover:bg-surface-2 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-subtle-foreground">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
              </svg>
              {conversation.name ? "Ret samtalens navn" : "Giv samtalen et navn"}
            </button>
          )}

          {canAddParticipants && (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setAddOpen(true);
              }}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left text-foreground hover:bg-surface-2 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-subtle-foreground">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
              Tilføj deltagere
            </button>
          )}

          <div className="border-t border-border mt-1 pt-1">
            {canBlock &&
              (isBlocked ? (
                <button
                  type="button"
                  onClick={handleUnblock}
                  disabled={unblock.isPending}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left text-foreground hover:bg-surface-2 transition-colors disabled:opacity-50"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-subtle-foreground">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="9 12 12 15 16 10" />
                  </svg>
                  Fjern blokering
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirming("block");
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left text-destructive hover:bg-destructive-muted transition-colors"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                  </svg>
                  Bloker bruger
                </button>
              ))}

            {conversation.can.report && (
            <button
              type="button"
              onClick={() => {
                if (conversation.isGroup) {
                  // Whom? Guessing here is what made a group report land on
                  // whoever happened to be listed first.
                  setPickingReportTarget(true);
                  return;
                }
                setMenuOpen(false);
                setReportUserId(reportTarget?.id);
                setReportOpen(true);
              }}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left text-destructive hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
              </svg>
              {conversation.isGroup ? "Anmeld en deltager" : "Anmeld for spam"}
            </button>
            )}

            {/* "Slet samtale" IS a decline — the same write, and the same
                capability. */}
            {conversation.can.decline && (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setConfirming("delete");
              }}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left text-destructive hover:bg-destructive-muted transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
              Slet samtale
            </button>
            )}

            {conversation.can.leave && (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setConfirming("leave");
              }}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left text-destructive hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Forlad samtalen
            </button>
            )}
          </div>
        </div>
      )}

      {error && anchored && (
        <p className={`absolute right-0 ${dropdownSide} w-56 text-xs text-destructive bg-surface rounded-lg border border-red-200 dark:border-red-900 px-3 py-2 shadow-lg z-50`}>
          {error}
        </p>
      )}

      {nameDraft !== null && (
        <Dialog
          open
          onClose={() => setNameDraft(null)}
          align="top"
          label="Navngiv samtalen"
          className="p-5"
        >
          <h2 className="text-sm font-semibold text-foreground mb-1">
            Navngiv samtalen
          </h2>
          <p className="text-xs text-subtle-foreground mb-3">
            Alle i samtalen ser navnet. Lad feltet stå tomt for at bruge
            deltagernes navne igen.
          </p>
          <Input
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            maxLength={MAX_NAME_LENGTH}
            placeholder={conversationTitle({
              name: null,
              participants: conversation.participants,
            })}
            aria-label="Samtalens navn"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void handleRename();
              }
            }}
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="secondary"
              onClick={() => setNameDraft(null)}
              disabled={rename.isPending}
            >
              Annuller
            </Button>
            <Button onClick={handleRename} disabled={rename.isPending}>
              {rename.isPending ? "Gemmer…" : "Gem"}
            </Button>
          </div>
        </Dialog>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-24">
          <div className="w-full max-w-md bg-surface rounded-2xl border border-border p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-foreground mb-3">
              Tilføj deltagere
            </h2>
            <ParticipantPicker
              value={picked}
              onChange={setPicked}
              excludeIds={conversation.participants.map((p) => p.user.id)}
              maxOthers={Math.max(0, 20 - conversation.participantCount)}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setAddOpen(false);
                  setPicked([]);
                }}
                className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Annuller
              </button>
              <button
                type="button"
                onClick={handleAdd}
                disabled={picked.length === 0 || add.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {add.isPending ? "Tilføjer…" : "Tilføj"}
              </button>
            </div>
          </div>
        </div>
      )}

      {reportOpen && (
        <ReportDialog
          conversationId={conversation.id}
          userId={reportUserId}
          // Blocking is opt-in from an established conversation; the accept
          // gate's own report defaults it on, because there declining and
          // blocking are usually the same intent.
          defaultBlock={false}
          onClose={() => setReportOpen(false)}
          // The report's own "block and hide" checkbox ends the relationship
          // exactly like the standalone Bloker/Slet actions do — same onGone.
          onReported={(result) => {
            if (result.blocked) onGone?.();
          }}
        />
      )}

      <ConfirmDialog
        open={confirming !== null}
        title={
          confirming === "block"
            ? `Bloker ${blockTargetName}?`
            : confirming === "delete"
              ? "Slet samtalen?"
              : "Forlad samtalen?"
        }
        description={
          confirming === "block"
            ? `${blockTargetName} kan ikke længere sende dig beskeder, og samtalen forsvinder fra din indbakke. Du kan fjerne blokeringen igen fra denne menu.`
            : confirming === "delete"
              ? "Samtalen fjernes fra din indbakke. Det ændrer ikke noget for de andre deltagere."
              : "Du kan ikke se den igen."
        }
        confirmLabel={
          confirming === "block" ? "Bloker" : confirming === "delete" ? "Slet" : "Forlad"
        }
        pending={confirmPending}
        pendingLabel={
          confirming === "block" ? "Blokerer…" : confirming === "delete" ? "Sletter…" : "Forlader…"
        }
        onConfirm={handleConfirmed}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}
